import type {
	PublicFormDefinitionDto,
	PublicFormSubmitRequestDto,
	PublicFormSubmitResponseDto,
	PublicFormUploadFieldsDto,
	PublicFormUploadResponseDto,
} from '@supply-mover-2-0/shared';
import { getPortalTenantHeader } from '../lib/tenantResolver';

/**
 * The storefront's client for the public Workd Forms endpoints (docs/claude/forms/01-design.md
 * §3). Both calls are unauthenticated, so they cannot use `portalFetch`, which adds an
 * Authorization header and runs the token-refresh retry.
 *
 * The `X-Portal-Tenant` header picks the tenant DATABASE and must be the brand-qualified
 * account alias `getPortalTenantHeader()` returns (`<brand>-<tenant>`, or the bare tenant for
 * the default brand). On a custom domain it returns null and the header is left off, so the
 * server resolves the tenant from the Origin. The BRAND (which storefront's form) always
 * comes from the Origin, never from the body.
 */
function portalTenantHeaders(base: Record<string, string> = {}): HeadersInit {
	const tenantHeader = getPortalTenantHeader();
	return tenantHeader != null
		? { ...base, 'X-Portal-Tenant': tenantHeader }
		: base;
}

const formsUrl = (formKey: string): string =>
	`${import.meta.env.VITE_API_URL}/api/portal/forms/${encodeURIComponent(formKey)}`;

/** The form as the page draws it, or null when the storefront has no such active form. */
export async function fetchFormDefinition(
	formKey: string,
): Promise<PublicFormDefinitionDto | null> {
	const response = await fetch(formsUrl(formKey), {
		headers: portalTenantHeaders(),
	});
	if (response.status === 404) {
		return null;
	}
	if (!response.ok) {
		throw new Error(`Form ${formKey} could not be loaded (${response.status})`);
	}
	return (await response.json()) as PublicFormDefinitionDto;
}

export interface FormSubmitOutcomeModel {
	status: number;
	body: PublicFormSubmitResponseDto;
}

/**
 * Sends a filled-in form. Refusals (validation, verification, rate limit) come back as a
 * structured body with a 4xx status, so this does not throw on them; the page shows them.
 * Only a network failure or an unreadable reply throws.
 */
export async function submitForm(
	formKey: string,
	body: PublicFormSubmitRequestDto,
): Promise<FormSubmitOutcomeModel> {
	const response = await fetch(`${formsUrl(formKey)}/submit`, {
		method: 'POST',
		headers: portalTenantHeaders({ 'Content-Type': 'application/json' }),
		body: JSON.stringify(body),
	});
	const json = (await response
		.json()
		.catch(() => null)) as PublicFormSubmitResponseDto | null;
	if (json == null || typeof json.success !== 'boolean') {
		throw new Error(
			`Unexpected reply from the form endpoint (${response.status})`,
		);
	}
	return { status: response.status, body: json };
}

export interface FormUploadOutcomeModel {
	status: number;
	body: PublicFormUploadResponseDto;
}

/**
 * PD-16713 — uploads one file for a file question, as the visitor picks it, as multipart
 * form data (the browser sets the boundary, so no Content-Type header is set here).
 * Refusals (the file, the spam check, a rate limit) come back as a structured body with a
 * 4xx status and are not thrown. A body the server never wrote (the 413 for a file past
 * the largest any question takes, refused before the file is read) is answered as that
 * refusal; only a network failure throws.
 */
export async function uploadFormFile(
	formKey: string,
	fields: PublicFormUploadFieldsDto,
	file: File,
): Promise<FormUploadOutcomeModel> {
	const body = new FormData();
	body.append('submissionId', fields.submissionId);
	body.append('uploadId', fields.uploadId);
	body.append('fieldKey', fields.fieldKey);
	if (fields.turnstileToken != null) {
		body.append('turnstileToken', fields.turnstileToken);
	}
	// Last, so the server has read the small parts by the time the file arrives.
	body.append('file', file, file.name);

	const response = await fetch(`${formsUrl(formKey)}/uploads`, {
		method: 'POST',
		headers: portalTenantHeaders(),
		body,
	});
	const json = (await response
		.json()
		.catch(() => null)) as PublicFormUploadResponseDto | null;
	if (json != null && typeof json.success === 'boolean') {
		return { status: response.status, body: json };
	}
	if (response.status === 413) {
		return {
			status: 413,
			body: {
				success: false,
				message: 'The file is too large.',
				errors: { [fields.fieldKey]: 'file_too_large' },
			},
		};
	}
	throw new Error(`Unexpected reply from the upload endpoint (${response.status})`);
}
